import {
  CheckCircle2, Activity, Circle, Lock, AlertTriangle,
  ClipboardCheck, Landmark, Code, Database, TestTubeDiagonal,
  GraduationCap, Calculator, FolderKanban, SlidersHorizontal,
} from "lucide-react";
import type { AgentPipelineEntry, AgentPipelineStatus } from "@/types/api";


// ── Fonte única de verdade para `AgentPipelineStatus` ───────────────────────
//
// Mapeia `pipeline_status` → tom → (rótulo, cor), padronizando a escala visual.
// Evita inconsistências onde diferentes partes da interface exibiam
// rótulos conflitantes ou definiam cores baseadas no nome da fase em vez
// do estado real do pipeline.
//
// Regra: componentes que renderizam estado utilizam este contrato centralizado
// e nunca definem rótulo ou cor individualmente.

export const PIPELINE_TONES = [
  "done",
  "running",
  /** #854: terminou e espera uma decisão de gente. Não está correndo. */
  "decision",
  "next",
  "waiting",
  "failed",
] as const;

export type PipelineTone = (typeof PIPELINE_TONES)[number];

/** `AgentPipelineStatus` → tom. `ready` e `running` são tons **distintos**. */
export const pipelineStatusTone: Record<AgentPipelineStatus, PipelineTone> = {
  completed: "done",
  running: "running",
  awaiting_decision: "decision",
  ready: "next",
  locked: "waiting",
  failed: "failed",
};

// Chaves i18n, não texto pronto: o rótulo é um só, e é este.
const TONE_LABEL_KEY: Record<PipelineTone, string> = {
  done: "Completed",
  running: "Running",
  decision: "Awaiting your decision",
  next: "Next up",
  waiting: "Waiting",
  failed: "Failed",
};

export function pipelineToneLabel(tone: PipelineTone, t: (k: string) => string): string {
  return t(TONE_LABEL_KEY[tone]);
}

export function pipelineStatusLabel(
  status: AgentPipelineStatus,
  t: (k: string) => string,
): string {
  return pipelineToneLabel(pipelineStatusTone[status], t);
}

export interface PipelineToneStyle {
  /** Cor sólida do ponto/indicador, para `style` inline. */
  dot: string;
  /** Halo — só o que está em curso pulsa. */
  glow: boolean;
  /** Classe de cor do texto. */
  text: string;
  /** Classes do badge de estado. */
  pill: string;
  /** Gradiente da barra de progresso da fase. */
  bar: string;
}

export const pipelineToneStyle: Record<PipelineTone, PipelineToneStyle> = {
  done: {
    dot: "#27a155",
    glow: true,
    text: "text-[#27a155]",
    pill: "bg-[rgba(39,161,85,0.1)] text-[#27a155]",
    bar: "linear-gradient(90deg,#27a155,#5fcc86)",
  },
  running: {
    dot: "#ffc83d",
    glow: true,
    text: "text-[#b57a00]",
    pill: "bg-[rgba(255,200,61,0.15)] text-[#b57a00]",
    bar: "linear-gradient(90deg,#ffc83d,#ffb300)",
  },
  decision: {
    // Laranja próprio, e não o âmbar do `running`: a regra do S-075 é um
    // estado, uma cor. Fica na mesma família de atenção, sem o pulso — o que
    // está em curso é a decisão, não a execução.
    dot: "#e8760c",
    glow: false,
    text: "text-[#b35a00]",
    pill: "bg-[rgba(232,118,12,0.12)] text-[#b35a00]",
    bar: "linear-gradient(90deg,#e8760c,#f5a04a)",
  },
  next: {
    dot: "#1d4ea0",
    glow: false,
    text: "text-[#1d4ea0]",
    pill: "bg-[rgba(29,78,160,0.1)] text-[#1d4ea0]",
    bar: "linear-gradient(90deg,#1d4ea0,#6d8fc4)",
  },
  waiting: {
    dot: "#b2bccb",
    glow: false,
    text: "text-[#7b8799]",
    pill: "bg-[#f2f4f7] text-[#7b8799]",
    bar: "linear-gradient(90deg,#d4dae3,#e8ebf0)",
  },
  failed: {
    dot: "#d4353c",
    glow: false,
    text: "text-[#d4353c]",
    pill: "bg-[rgba(212,53,60,0.1)] text-[#d4353c]",
    bar: "linear-gradient(90deg,#d4353c,#e8767b)",
  },
};

/** Ícone por estado. Mapa literal: estado novo exige uma linha nova aqui. */
export const pipelineStatusIcon: Record<AgentPipelineStatus, React.ElementType> = {
  completed: CheckCircle2,
  running: Activity,
  // Quem decide é uma pessoa: o ícone pede ação, não progresso.
  awaiting_decision: ClipboardCheck,
  ready: Circle,
  locked: Lock,
  failed: AlertTriangle,
};

/** Tom que resume uma fase. `"empty"` é ausência de agente, não um estado.
 *
 *  Precedência: falha domina (é o que exige ação), depois o que está em curso,
 *  depois o que vem a seguir. "Concluída" só quando **todos** concluíram. */
export function phaseTone(agents: AgentPipelineEntry[]): PipelineTone | "empty" {
  if (agents.length === 0) return "empty";
  const has = (s: AgentPipelineStatus) => agents.some((a) => a.pipeline_status === s);
  if (has("failed")) return "failed";
  // #854: decisão pendente vem logo depois da falha, pela mesma regra de
  // precedência já escrita acima — é o que exige ação, e só a pessoa destrava.
  // O que está em curso não pede nada de ninguém.
  if (has("awaiting_decision")) return "decision";
  if (has("running")) return "running";
  if (has("ready")) return "next";
  if (agents.every((a) => a.pipeline_status === "completed")) return "done";
  return "waiting";
}

// ── Agent icon map ──────────────────────────────────────────────────────────

export const agentIconMap: Record<string, React.ElementType> = {
  ClipboardCheck, Landmark, Code, Database, TestTubeDiagonal, GraduationCap,
  Calculator, FolderKanban, SlidersHorizontal,
};
